// data/GetAsignaturasOrganizacion.tsx (o donde lo tengas)
// Este componente debe devolver a.id = id_nivel_asignatura

import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { UseSetLenguajePorPais } from "../hooks/UseSetLenguajePorPais";
import { useAuthContext } from "../hooks/Auth";
import type { FormattedAsignatura } from "../interfaces/ObjetivosAprendizaje/iAsignaturas";
import { get_asignaturas_organizacion } from "../services/api";

type SelectedAsignaturaProp = string | FormattedAsignatura | null | undefined;
type SelectedNivelProp = string | { id?: string; nombre?: string } | null | undefined;

interface Props {
  nivelId: SelectedNivelProp; // ✅ requerido
  onAsignaturaChange: (asignatura: FormattedAsignatura) => void;
  selectedAsignatura: SelectedAsignaturaProp; // puede ser string u objeto
}

const EMPTY_ASIGNATURA: FormattedAsignatura = { id: "", nombre: "" };

function normalizeId(value: SelectedNivelProp | SelectedAsignaturaProp): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "id" in value) {
    return String((value as any).id ?? "");
  }
  return "";
}

const GetAsignaturasOrganizacion: FC<Props> = ({
  nivelId,
  onAsignaturaChange,
  selectedAsignatura,
}) => {
  const [data, setData] = useState<FormattedAsignatura[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");

  const { user } = useAuthContext();
  const { t } = useTranslation();

  UseSetLenguajePorPais();

  const IS_FROM_URUGUAY = user?.pais === "UY";
  const nivelIdStr = useMemo(() => normalizeId(nivelId), [nivelId]);

  // mantener controlado el select
  useEffect(() => {
    setSelectedId(normalizeId(selectedAsignatura));
  }, [selectedAsignatura]);

  useEffect(() => {
    let isMounted = true;

    async function fetchAsignaturasPorNivel() {
      if (!nivelIdStr) {
        if (isMounted) {
          setData([]);
          setSelectedId("");
        }
        onAsignaturaChange(EMPTY_ASIGNATURA);
        return;
      }

      try {
        const { data: responseData, status } =
          await get_asignaturas_organizacion(nivelIdStr);

        if (status !== 200) {
          console.error("GetAsignaturasOrganizacion: status no exitoso", {
            status,
            responseData,
          });
          if (isMounted) {
            setData([]);
            setSelectedId("");
          }
          onAsignaturaChange(EMPTY_ASIGNATURA);
          return;
        }

        // tu endpoint puede devolver array directo o envuelto en body
        const raw = Array.isArray(responseData?.body)
          ? responseData.body
          : Array.isArray(responseData)
          ? responseData
          : [];

        /**
         * ✅ CLAVE:
         * - id = id_nivel_asignatura  (esto es lo que necesita GetObjetivosAprendizajeOrganizacion)
         * - nombre = asignatura       (texto visible)
         * Opcional: guardamos id_asignatura en una propiedad extra por si luego te sirve
         */
        const asignaturasArray: FormattedAsignatura[] = raw
          .map((row: any) => {
            const idNivelAsignatura = String(row?.id_nivel_asignatura ?? "");
            const nombreAsignatura = String(row?.asignatura ?? "");

            const out: any = {
              id: idNivelAsignatura,
              nombre: nombreAsignatura,
              id_asignatura: String(row?.id_asignatura ?? ""), // extra opcional
            };

            return out as FormattedAsignatura;
          })
          .filter((x: any) => x.id && x.nombre);

        if (!isMounted) return;

        setData(asignaturasArray);

        // si lo seleccionado no existe en el nuevo set, limpiar
        const currentId = normalizeId(selectedAsignatura);
        const exists = asignaturasArray.some((a: any) => a.id === currentId);
        if (!exists) {
          setSelectedId("");
          onAsignaturaChange(EMPTY_ASIGNATURA);
        }
      } catch (error) {
        console.error("GetAsignaturasOrganizacion: error llamando API", error);
        if (isMounted) {
          setData([]);
          setSelectedId("");
        }
        onAsignaturaChange(EMPTY_ASIGNATURA);
      }
    }

    fetchAsignaturasPorNivel();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivelIdStr]);

  const handleAsignaturaChange = (event: SelectChangeEvent<any>) => {
    const nextId = String(event.target.value ?? "");
    setSelectedId(nextId);

    const asignaturaSeleccionada =
      (data.find((item: any) => item.id === nextId) as any) ?? EMPTY_ASIGNATURA;

    onAsignaturaChange(asignaturaSeleccionada);
  };

  return (
    <Grid item xs={12}>
      <Box>
        <Typography
          sx={{
            fontFamily: "montserrat",
            fontSize: "1.25rem",
            fontWeight: "bold",
            color: "#336B87",
          }}
        >
          {!IS_FROM_URUGUAY ? t("asignatura") : "Materia *"}
        </Typography>

        <FormControl fullWidth>
          <Select
            labelId="select-asignatura-organizacion"
            id="select-asignatura-organizacion"
            variant="outlined"
            value={selectedId}
            displayEmpty
            onChange={handleAsignaturaChange}
            disabled={!nivelIdStr}
            sx={{
              fontFamily: "montserrat",
              fontSize: "14px",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& fieldset": { borderColor: "#BDBDBD" },
              "&:hover fieldset": { borderColor: "#9E9E9E" },
              "&.Mui-focused fieldset": { borderColor: "#005559" },
              "& .MuiSelect-select": {
                color: selectedId ? "#005559" : "#B3D1D1",
              },
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            <option hidden value="">
              {!nivelIdStr
                ? "Selecciona primero un nivel"
                : "Selecciona por ejemplo: Matemáticas"}
            </option>

            {data.map((asignatura: any) => (
              <MenuItem
                key={asignatura.id}
                value={asignatura.id}
                sx={{ color: "#005559" }}
              >
                <Typography
                  sx={{
                    fontFamily: "montserrat",
                    fontSize: "0.8rem",
                    color: "#336B87",
                  }}
                >
                  {asignatura.nombre}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Grid>
  );
};

export default GetAsignaturasOrganizacion;
